// src/hooks/useUserProfile.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { ref, get, onValue, off } from 'firebase/database';
import { db } from '../lib/firebase';

interface UserProfile {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  credits?: number;
  createdAt?: string;
  updatedAt?: string;
  subscriptionStatus?: string;
  subscriptionId?: string;
  stripeCustomerId?: string;
}

export const useUserProfile = (userId: string | undefined) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const creditsRef = useRef<number>(0);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    console.log('[useUserProfile] Initialisation pour userId:', userId);

    // Fonction pour calculer les crédits totaux
    const calculateTotalCredits = async (): Promise<number> => {
      try {
        const creditsDbRef = ref(db, `credits/${userId}`);
        const snapshot = await get(creditsDbRef);

        let totalCredits = 0;
        if (snapshot.exists()) {
          const creditsData = snapshot.val();
          totalCredits = Object.values(creditsData).reduce((sum: number, credit: any) => {
            return sum + (credit.amount || 0);
          }, 0);
        }

        console.log('[useUserProfile] Crédits calculés:', totalCredits);
        return totalCredits;
      } catch (err) {
        console.error('[useUserProfile] Erreur calcul crédits:', err);
        return 0;
      }
    };

    // Charger les données initiales (profil + crédits)
    const loadInitialData = async () => {
      try {
        console.log('[useUserProfile] Chargement initial des données');

        const profileRef = ref(db, `profiles/${userId}`);
        const profileSnapshot = await get(profileRef);

        const totalCredits = await calculateTotalCredits();
        creditsRef.current = totalCredits;

        const profileData: UserProfile = {
          ...(profileSnapshot.exists() ? profileSnapshot.val() : {}),
          credits: totalCredits,
          createdAt: profileSnapshot.exists() ? profileSnapshot.val().createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        console.log('[useUserProfile] Données initiales chargées:', {
          credits: profileData.credits,
          subscriptionStatus: profileData.subscriptionStatus
        });

        setProfile(profileData);
        setLoading(false);
      } catch (err) {
        console.error('[useUserProfile] Erreur lors du chargement initial:', err);
        setError('Erreur lors du chargement du profil');
        setLoading(false);
      }
    };

    loadInitialData();

    // Listener pour les changements de crédits
    const creditsDbRef = ref(db, `credits/${userId}`);
    const unsubscribeCredits = onValue(creditsDbRef, (snapshot) => {
      let totalCredits = 0;
      if (snapshot.exists()) {
        const creditsData = snapshot.val();
        totalCredits = Object.values(creditsData).reduce((sum: number, credit: any) => {
          return sum + (credit.amount || 0);
        }, 0);
      }

      console.log('[useUserProfile] Mise à jour crédits en temps réel:', totalCredits);
      creditsRef.current = totalCredits;

      setProfile((prev) => {
        if (!prev) return null;
        return { ...prev, credits: totalCredits };
      });
    }, (err) => {
      console.error('[useUserProfile] Erreur listener crédits:', err);
    });

    // Listener pour les changements du profil
    const profileDbRef = ref(db, `profiles/${userId}`);
    const unsubscribeProfile = onValue(profileDbRef, (snapshot) => {
      if (snapshot.exists()) {
        const profileData = snapshot.val();

        console.log('[useUserProfile] Mise à jour profil en temps réel:', {
          subscriptionStatus: profileData.subscriptionStatus
        });

        setProfile((prev) => ({
          ...profileData,
          credits: creditsRef.current,
          updatedAt: new Date().toISOString()
        }));
      }
    }, (err) => {
      console.error('[useUserProfile] Erreur listener profil:', err);
    });

    // Nettoyer les listeners lors du démontage
    return () => {
      console.log('[useUserProfile] Nettoyage des listeners');
      off(creditsDbRef, 'value', unsubscribeCredits);
      off(profileDbRef, 'value', unsubscribeProfile);
    };
  }, [userId]);

  // Fonction pour forcer le rechargement
  const reloadCredits = useCallback(async () => {
    if (!userId) return;

    console.log('[useUserProfile] Rechargement forcé des crédits');

    try {
      const creditsDbRef = ref(db, `credits/${userId}`);
      const snapshot = await get(creditsDbRef);

      let totalCredits = 0;
      if (snapshot.exists()) {
        const creditsData = snapshot.val();
        totalCredits = Object.values(creditsData).reduce((sum: number, credit: any) => {
          return sum + (credit.amount || 0);
        }, 0);
      }

      console.log('[useUserProfile] Crédits rechargés:', totalCredits);
      creditsRef.current = totalCredits;

      setProfile((prev) => {
        if (!prev) return null;
        return { ...prev, credits: totalCredits };
      });
    } catch (err) {
      console.error('[useUserProfile] Erreur rechargement crédits:', err);
    }
  }, [userId]);

  return { profile, loading, error, reloadCredits };
};
